import type { AppEndpointBuilder } from 'store/types';
import { mergeCampaignParams } from 'utils/campaign';
import { buildCurrentAppURL } from 'utils/url';

import {
  toAuthenticationExchangeModel,
  toAuthenticationModel,
  toCaptchaModel,
  toMultiFactorModel,
  toSessionModel,
  toSessionTokenModel,
  toTokenModel,
} from './helpers';
import type {
  AuthenticationModel,
  CaptchaModel,
  MultiFactorModel,
  SessionModel,
  SessionTokenModel,
  TokenModel,
} from './models';
import type {
  ExchangeSessionTokenPayload,
  ForgotPasswordPayload,
  MultiFactorPayload,
  ResetPasswordPayload,
  SignInPayload,
  SignUpPayload,
} from './payloads';
import type { MultiFactorProvider } from './types';

export const authEndpoints = (builder: AppEndpointBuilder) => ({
  // #region Queries
  getCaptcha: builder.query<CaptchaModel, void>({
    query: () => ({
      url: 'captcha',
      method: 'GET',
    }),
    providesTags: ['Captcha'],
    transformResponse: toCaptchaModel,
  }),

  getMultiFactorStatus: builder.query<MultiFactorModel, MultiFactorProvider | void>({
    query: (preferProvider) => ({
      url: buildCurrentAppURL('2fa-status'),
      method: 'GET',
      params: { preferProvider },
    }),
    transformResponse: toMultiFactorModel,
  }),

  getSession: builder.query<SessionModel, void>({
    query: () => ({
      url: 'session',
      method: 'GET',
    }),
    providesTags: ['Session'],
    transformResponse: toSessionModel,
  }),
  // #endregion Queries

  // #region Mutations
  exchangeSessionToken: builder.mutation<AuthenticationModel, ExchangeSessionTokenPayload>({
    query: ({ queryParams, ...data }) => ({
      url: 'exchange-session-token',
      method: 'POST',
      data: { ...mergeCampaignParams(queryParams), ...data },
    }),
    transformResponse: toAuthenticationExchangeModel,
  }),

  forgotPassword: builder.mutation<void, ForgotPasswordPayload>({
    query: (data) => ({
      url: 'forgot-password-post',
      method: 'POST',
      data,
    }),
  }),

  login: builder.mutation<AuthenticationModel, SignInPayload>({
    query: ({ queryParams, ...data }) => ({
      url: buildCurrentAppURL('login-check'),
      method: 'POST',
      data: { ...mergeCampaignParams(queryParams), ...data },
    }),
    transformResponse: toAuthenticationModel,
  }),

  logout: builder.mutation<void, void>({
    query: () => ({
      url: 'logout',
      method: 'POST',
    }),
  }),

  multiFactorVerification: builder.mutation<TokenModel, MultiFactorPayload>({
    query: ({ preferProvider, ...data }) => ({
      url: buildCurrentAppURL('2fa-check-api'),
      method: 'POST',
      data,
      params: { preferProvider },
    }),
    transformResponse: toTokenModel,
  }),

  register: builder.mutation<TokenModel, SignUpPayload>({
    query: ({ queryParams, ...data }) => ({
      url: buildCurrentAppURL('registration'),
      method: 'POST',
      data: { ...mergeCampaignParams(queryParams), ...data },
    }),
    transformResponse: toTokenModel,
  }),

  resetPassword: builder.mutation<void, ResetPasswordPayload>({
    query: ({ code, ...data }) => ({
      url: `reset-password-post/${code}`,
      method: 'POST',
      data,
    }),
  }),

  switchProduct: builder.mutation<SessionTokenModel, void>({
    query: () => ({
      url: 'generate-session-token',
      method: 'POST',
    }),
    transformResponse: toSessionTokenModel,
  }),
  // #endregion Mutations
});
